// Lib
import { nanoid } from 'nanoid';
import { isEmpty } from 'lodash/fp';

// Utils
import {
    formatIdeaObjectForRender,
    formatIdeaObjectAsPromptShortcut,
} from '../../../../../../../common/ai/aiResponseFormattingUtils';
import { parseStructuredTextIdeas } from '../../../../../../../common/ai/aiIdeaConversionUtils';

// Types
import { AiConversationMessage, AiIdeaType, AiIdea } from '../../../../../../../common/ai/aiConversationTypes';
import { AiLocalAiConversation, AiHistoryEntry, AiResultType } from './aiToolStateTypes';

// Constants
import { AIConversationResultType } from './aiToolStateConstants';
import { PromptShortcut } from '../../../../../../../common/ai/aiPromptShortcutTypes';

/**
 * Gets the number of conversation submissions in the provided conversation.
 */
export const getConversationSubmissionsCount = (conversation: AiLocalAiConversation): number => {
    if (isEmpty(conversation.results)) return 0;

    return conversation.results.filter(
        (result: AiResultType) => result.elementType === AIConversationResultType.PROMPT_HISTORY,
    ).length;
};

const convertUserRoleMessageToResultObject = (message: AiConversationMessage): AiHistoryEntry => ({
    uuid: nanoid(),
    elementType: AIConversationResultType.PROMPT_HISTORY,
    text: message.content,
});

const convertAssistantRoleMessageToResultObjects = (message: AiConversationMessage): AiResultType[] => {
    try {
        const ideas = parseStructuredTextIdeas(message.content).filter(
            (idea: AiIdea) => idea.type !== AiIdeaType.FOLLOWUP,
        );

        return ideas.map(formatIdeaObjectForRender) as AiResultType[];
    } catch {
        // ignore failed parsing / formatting — it's expected that some messages will be garbled
    }

    return [];
};

const convertSingleMessageToResultObjects = (message: AiConversationMessage): AiResultType[] => {
    switch (message.role) {
        case 'user':
            return [convertUserRoleMessageToResultObject(message)];
        case 'assistant':
            return convertAssistantRoleMessageToResultObjects(message);
        default:
            return [];
    }
};

export const convertConversationMessagesToResultObjects = (messages: AiConversationMessage[]): AiResultType[] =>
    messages.flatMap(convertSingleMessageToResultObjects);

export const convertConversationMessagesToFollowupPromptShortcuts = (
    messages: AiConversationMessage[],
): PromptShortcut[] => {
    return messages
        .filter((message) => message.role === 'assistant')
        .slice(-1)
        .flatMap((message): PromptShortcut[] => {
            try {
                const followupIdeas = parseStructuredTextIdeas(message.content).filter(
                    (idea: AiIdea) => idea.type === AiIdeaType.FOLLOWUP,
                );

                return followupIdeas.map(formatIdeaObjectAsPromptShortcut) as PromptShortcut[];
            } catch {
                // ignore failed parsing / formatting — it's expected that some messages will be garbled
            }

            return [];
        });
};
